import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { omit } from 'lodash';
import { take } from 'rxjs/operators';

import { Campaign } from 'ideta-library/lib/common/aicm';
import { ProductMeta } from 'ideta-library/lib/common/bot';
import { Page, PageConnection } from 'ideta-library/lib/common/channels/meta';
import { OpenAICredentials } from 'ideta-library/lib/common/lexicon';
import { ConnectAICMPagePayload, RepliesInboxLinkPayload } from 'ideta-library/lib/common/request';
import { DeepPartial } from 'ideta-library/lib/utils/utils';

import { environment } from '../../../../environments/environment';

import { MetaService } from '../meta/meta.service';
import { UserSessionService } from '../session/user-session.service';
import { RequestsService } from './../../../shared/services/requests/requests.service';

import { DisplayPage } from '../../components/pages-manager/pages-manager.component';

export type AvailableChannels = 'page' | 'instagram';

export type DisplayPageConnection = PageConnection & {
  spinningLink?: boolean;
  linkError?: boolean;
};

@Injectable({
  providedIn: 'root'
})
export class AutoReplyService {
  private fullFbUrl: string;
  private fullInstaUrl: string;

  constructor(
    private db: AngularFireDatabase,
    private _user: UserSessionService,
    private httpClient: HttpClient,
    private requestsService: RequestsService,
    private metaService: MetaService
  ) {
    this.fullFbUrl = `${environment.facebook.url}/${environment.facebook.version}`;
    this.fullInstaUrl = `${environment.instagram.url}`;
  }

  upsertCampaign(campaign: DeepPartial<Campaign>, operation: string, status: boolean): Promise<boolean> {
    // ###M
    // console.log('final updated campaign', campaign);
    return (operation === 'update' ? Promise.resolve(false) : this.checkIfCampaignExists(campaign.id)).then((exists: boolean) => {
      // ###M
      // console.log('exists : ', exists, operation, postData);
      if (!exists) {
        return this.db
          .object(`/auto-replies/${this._user.id}/${campaign.id}`)
          .update({ ...campaign, userId: this._user.id, status }) // TODO: the campaign obejct should clean from all the properties that only belong to the front display
          .then(() => true);
      } else {
        return Promise.resolve(false);
      }
    });
  }

  async getPagePosts(page_id: string, access_token: string): Promise<any[]> {
    const postsData: any = await this.httpClient.get(`${this.fullFbUrl}/${page_id}/published_posts?access_token=${access_token}`).toPromise();
    const getPostsInfos = (postsData?.data || []).map(post => this.getFbPostInfos(post.id, access_token));
    return Promise.all(getPostsInfos);
  }

  getFbPostInfos(post_id: string, access_token: string) {
    if (post_id !== 'all_all') {
      const url = `${this.fullFbUrl}/${post_id}?fields=id,message,properties, actions, created_time,permalink_url&access_token=${access_token}`;

      return this.httpClient
        .get(url)
        .toPromise()
        .catch(err => console.log(err));
    } else {
      return Promise.resolve();
    }
  }

  async isAccessTokenValid(access_token: string, channel?: string): Promise<{ valid: boolean; error?: any }> {
    const url =
      channel === 'instagram' ? `${this.fullInstaUrl}/me?access_token=${access_token}` : `${this.fullFbUrl}/me?access_token=${access_token}`;

    try {
      await this.httpClient.get(url).toPromise();
      return { valid: true };
    } catch (error) {
      console.error('Error validating access token:', error);
      return { valid: false, error: error };
    }
  }

  getInstaMediaInfos(ig_media_id: string, access_token: string) {
    if (ig_media_id !== 'all') {
      const url = `${this.fullInstaUrl}/${ig_media_id}?fields=media_url,owner,shortcode,caption,timestamp,media_type,media_product_type&access_token=${access_token}`;

      return this.httpClient.get(url).toPromise();
    } else {
      return Promise.resolve();
    }
  }

  getBusinessAccountMediaObjects(ig_user_id: string, access_token: string): Promise<any> {
    const url = `${this.fullInstaUrl}/${ig_user_id}/media?access_token=${access_token}`;
    return this.httpClient.get(url).toPromise();
  }

  getAdAccounts(access_token): Promise<any> {
    const url = `${this.fullFbUrl}/me/adaccounts?field=id&access_token=${access_token}`;
    return this.httpClient
      .get(url)
      .toPromise()
      .catch(_ => Promise.resolve([]));
  }

  getAds(adAccount, access_token): Promise<any> {
    const url = `${this.fullFbUrl}/${adAccount}/ads?limit=100&access_token=${access_token}`;
    return this.httpClient
      .get(url)
      .toPromise()
      .catch(_ => Promise.resolve([]));
  }

  getAdPosts(adId, access_token): Promise<any> {
    const url = `${this.fullFbUrl}/${adId}?fields=creative.fields(effective_object_story_id, effective_instagram_media_id)&limit=100&access_token=${access_token}`;
    return this.httpClient
      .get(url)
      .toPromise()
      .catch(_ => Promise.resolve([]));
  }

  deleteCampaign(userId: string, campaign: Campaign): Promise<void> {
    //console.log('team name : ', team_name);
    return this.db.object(`/auto-replies/${userId}/${campaign.id}`).remove();
  }

  async linkUpInbox(page: DisplayPage, customer: string, customer_details: any): Promise<{ success: boolean; linkId?: string; error?: any }> {
    const body: RepliesInboxLinkPayload = {
      channel: page.channel,
      pageInfos: this.cleanPage(page),
      customerId: customer || '',
      customerDetails: customer_details || {}
    };

    return this.requestsService.post<{ success: boolean; linkId: string }>('/auto-replies/inbox-link', body).catch(err => {
      return { success: false, error: err.error };
    });
  }

  validateApiKey({ apiKey, model }: Partial<OpenAICredentials>) {
    return this.requestsService.post('/lexicons/openai/check-credentials', { apiKey, model });
  }

  connectFacebookPage(page: DisplayPage): Promise<{ success: boolean; error?: string }> {
    const body: ConnectAICMPagePayload = {
      ...this.cleanPage(page),
      picture: typeof page.picture === 'string' ? page.picture : page.picture.data.url,
      userInfos: this.metaService.localFacebook
    };

    return this.requestsService
      .post(`auto-replies/${page.id}/connect/${page.channel === 'instagram' ? 'instagram' : 'facebook'}`, body)
      .then(() => ({ success: true }))
      .catch(e => {
        if (e.status === 422) return { success: false, error: e.error?.message };
        return { success: false };
      });
  }

  disconnectPage(pageId: string, channel: string): Promise<{ success: boolean }> {
    const location: ProductMeta = channel === 'instagram' ? 'instagram' : 'facebook';
    return this.requestsService.delete<{ success: boolean }>(`/auto-replies/${pageId}/disconnect/${location}`);
  }

  private cleanPage(page: DisplayPage): Partial<Page> {
    return omit(page, ['channel', 'connected', 'linkId']);
  }

  private checkIfCampaignExists(campaignId: string): Promise<any> {
    return this.db
      .object(`/auto-replies/${this._user.id}/${campaignId}`)
      .valueChanges()
      .pipe(take(1))
      .toPromise()
      .then((campaign: Campaign) => !!campaign);
  }

  // Could be usefull if the user wants to refresh his account infos
  // getPageBusinessAccount(page_id: string, access_token: string): Promise<any> {
  //   const url = `${this.fullFbUrl}/${page_id}?fields=instagram_business_account{id,username,profile_picture_url}&access_token=${access_token}`;
  //   return this.httpClient.get(url).toPromise();
  // }

  // Could be usefull if we restore the old instagram connection method
  // getPageBusinessAccounts(access_token: string): Promise<any> {
  //   let pageList = [];
  //   return new Promise(async (resolve, reject) => {
  //     let next = `${this.fullFbUrl}/me/accounts?fields=id,name,userId,access_token,instagram_business_account{id,username,profile_picture_url}&limit=100`;
  //     while (next) {
  //       let headers = new HttpHeaders().set('Authorization', `Bearer ${access_token}`);
  //       let response = await this.httpClient
  //         .get(next, { headers })
  //         .toPromise()
  //         .catch(error => reject(error));
  //       pageList = pageList.concat(response['data']);
  //       response['paging'] ? (next = response['paging']['next']) : (next = null);
  //     }
  //     if (pageList.length) {
  //       resolve(pageList.filter(page => !!page.instagram_business_account));
  //     } else resolve([]);
  //   });
  // }
}
